import React, { useState, useRef, useLayoutEffect } from 'react';
import StatusDropdown from '../../StatusDropdown/StatusDropdown';
import { STATUS_OPTIONS } from '../../StatusDropdown/statusOptions';
import Fireworks from './Fireworks';
import ToolbarStyleTooltip from '../../../../../ui/components/ToolbarStyleTooltip/ToolbarStyleTooltip';
import './TaskCheckbox.css';

// Flag to control blocked sequential task icon: true = upward arrow, false = horizontal line
const USE_UPWARD_ARROW_FOR_BLOCKED_TASKS = true;

function TaskCheckbox({
  task,
  isSequential,
  isSequentialDisabled,
  onCheckboxChange,
  onBlockedIconMouseEnter,
  onBlockedIconMouseLeave,
  onFocusTaskAfterStatusSelect,
  disableLongPress = false
}) {
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusDropdownPosition, setStatusDropdownPosition] = useState(null);
  const checkboxRef = useRef(null);
  const checkboxBoxRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const isLongPressingRef = useRef(false);
  const isSelectingStatusRef = useRef(false);
  const longPressSucceededRef = useRef(false);
  const [fireworksTrigger, setFireworksTrigger] = useState(0);
  const [fireworksPosition, setFireworksPosition] = useState({ x: 0, y: 0 });
  const [blockedTooltipVisible, setBlockedTooltipVisible] = useState(false);
  const [incompleteTooltipVisible, setIncompleteTooltipVisible] = useState(false);
  const [customStatusTooltipVisible, setCustomStatusTooltipVisible] = useState(false);
  const prevStatusRef = useRef(task.status);

  const isIncomplete = !task.status || task.status === 'incomplete';
  const isCustomStatus = task.status === 'wip' || task.status === 'waiting' || task.status === 'wont_do';
  const customStatusLabel = STATUS_OPTIONS.find(o => o.value === task.status)?.label ?? task.status;

  // Handle fireworks positioning after status change to complete (from checkbox or toolbar)
  useLayoutEffect(() => {
    if (task.status === 'complete' && prevStatusRef.current !== 'complete') {
      if (checkboxBoxRef.current) {
        const rect = checkboxBoxRef.current.getBoundingClientRect();
        // Calculate center accounting for any potential rounding issues
        const centerX = Math.round(rect.left + rect.width / 2);
        const centerY = Math.round(rect.top + rect.height / 2);
        setFireworksPosition({ x: centerX, y: centerY });
        // Trigger fireworks after position is set
        setTimeout(() => {
          setFireworksTrigger(prev => prev + 1);
        }, 10);
      }
    }
    prevStatusRef.current = task.status;
  }, [task.status]);

  // Check if task is incomplete and has a future due date
  const isIncompleteWithFutureDueDate = () => {
    // Don't show clock if task is blocked
    if (isSequentialDisabled) return false;
    
    // Task is incomplete if status is 'incomplete' or undefined/null
    const isIncomplete = !task.status || task.status === 'incomplete';
    
    if (!isIncomplete || !task.due_date) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dateStr = task.due_date.split('T')[0];
    const [year, month, day] = dateStr.split('-');
    const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    dueDate.setHours(0, 0, 0, 0);
    
    return dueDate.getTime() > today.getTime();
  };

  const getClockIcon = () => {
    // Clock center is at (8, 8) in a 16x16 viewBox
    // Hour hand pointing to 12 (straight up): from center to top
    // Minute hand pointing to 5 (150 degrees from 12): from center at 150 degrees
    // Using polar coordinates: x = centerX + radius * cos(angle), y = centerY + radius * sin(angle)
    // Note: SVG y-axis is inverted, so we need to adjust angles
    // 0 degrees = 12 o'clock = -90 degrees in standard math
    // 5 on clock = 150 degrees clockwise from 12 = -90 + 150 = 60 degrees in standard math
    
    const centerX = 8;
    const centerY = 8;
    const hourHandLength = 5.5; // Shorter hour hand
    const minuteHandLength = 6.5; // Longer minute hand
    
    // Hour hand: pointing to 12 (straight up, -90 degrees in standard math)
    const hourAngle = -90 * (Math.PI / 180);
    const hourEndX = centerX + hourHandLength * Math.cos(hourAngle);
    const hourEndY = centerY + hourHandLength * Math.sin(hourAngle);
    
    // Minute hand: pointing to 5 (150 degrees clockwise from 12)
    // In standard math: -90 + 150 = 60 degrees
    const minuteAngle = 60 * (Math.PI / 180);
    const minuteEndX = centerX + minuteHandLength * Math.cos(minuteAngle);
    const minuteEndY = centerY + minuteHandLength * Math.sin(minuteAngle);
    
    return (
      <svg className="custom-checkbox-clock-icon" viewBox="0 0 16 16" fill="none">
        {/* Hour hand pointing to 12 */}
        <line 
          x1={centerX} 
          y1={centerY} 
          x2={hourEndX} 
          y2={hourEndY} 
          stroke="#767676" 
          strokeWidth="2" 
          strokeLinecap="round"
        />
        {/* Minute hand pointing to 5 */}
        <line 
          x1={centerX} 
          y1={centerY} 
          x2={minuteEndX} 
          y2={minuteEndY} 
          stroke="#767676" 
          strokeWidth="1.8" 
          strokeLinecap="round"
        />
      </svg>
    );
  };

  const getStatusIcon = (status) => {
    if (status === 'complete') {
      return (
        <svg className="custom-checkbox-checkmark" viewBox="0 0 16 16" fill="none">
          <path d="M3 8 L6 11 L13 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    if (status === 'wip') {
      return (
        <svg className="custom-checkbox-status-icon" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill="#d97706" d="m294.28 256.9l-54.42-54.41a12 12 0 0 0-17 0L12.45 401a12 12 0 0 0-.27 17.2l66.05 66.28a12 12 0 0 0 17.22-.23l198.81-210.36a12 12 0 0 0 .02-16.99m205.05-57.57l-43.89-43.58a21.46 21.46 0 0 0-15.28-6.26a21.9 21.9 0 0 0-12.79 4.14c0-.43.06-.85.09-1.22c.45-6.5 1.15-16.32-5.2-25.22a258 258 0 0 0-24.8-28.74a.6.6 0 0 0-.08-.08c-13.32-13.12-42.31-37.83-86.72-55.94A139.6 139.6 0 0 0 257.56 32C226 32 202 46.24 192.81 54.68A53.4 53.4 0 0 0 176 86.17L192 96s8.06-2 13.86-3.39a62.7 62.7 0 0 1 18.45-1.15c13.19 1.09 28.79 7.64 35.69 13.09c11.7 9.41 17.33 22.09 18.26 41.09c.2 4.23-9.52 21.35-24.16 39.84a8 8 0 0 0 .61 10.62l45.37 45.37a8 8 0 0 0 11 .25c12.07-11 30.49-28 34.67-30.59c7.69-4.73 13.19-5.64 14.7-5.8a19.2 19.2 0 0 1 11.29 2.38a1.24 1.24 0 0 1-.31.95l-1.82 1.73l-.3.28a21.52 21.52 0 0 0 .05 30.54l43.95 43.68a8 8 0 0 0 11.28 0l74.68-74.2a8 8 0 0 0 .06-11.36"/>
        </svg>
      );
    }
    if (status === 'waiting') {
      return (
        <svg className="custom-checkbox-status-icon" viewBox="0 0 16 16" fill="none">
          <path d="M4 2 L12 2 L10 7 L6 7 Z" fill="#7c3aed"/>
          <line x1="8" y1="7" x2="8" y2="9" stroke="#7c3aed" strokeWidth="1.2" strokeLinecap="round"/>
          <path d="M6 9 L10 9 L12 14 L4 14 Z" fill="#7c3aed"/>
        </svg>
      );
    }
    if (status === 'wont_do') {
      return (
        <svg className="custom-checkbox-status-icon" viewBox="0 0 16 16" fill="none">
          <line x1="3" y1="3" x2="13" y2="13" stroke="#475569" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="13" y1="3" x2="3" y2="13" stroke="#475569" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    }
    return null;
  };

  const handleCheckboxMouseDown = (e) => {
    if (isSequentialDisabled) return;
    
    isLongPressingRef.current = false;
    longPressSucceededRef.current = false;
    
    const target = e.currentTarget;
    if (!target) return;
    
    const rect = target.getBoundingClientRect();
    
    longPressTimerRef.current = setTimeout(() => {
      isLongPressingRef.current = true;
      longPressSucceededRef.current = true;
      setStatusDropdownPosition({
        x: rect.left,
        y: rect.bottom + 4,
        triggerRect: { top: rect.top, bottom: rect.bottom }
      });
      setStatusDropdownOpen(true);
    }, 500);
  };

  const handleCheckboxMouseUp = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setTimeout(() => {
      isLongPressingRef.current = false;
      longPressSucceededRef.current = false;
    }, 100);
  };

  const handleCheckboxMouseLeave = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    isLongPressingRef.current = false;
    longPressSucceededRef.current = false;
  };

  const handleCheckboxContextMenu = (e) => {
    if (isSequentialDisabled) return;
    e.preventDefault();
    const target = e.currentTarget;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    setStatusDropdownPosition({
      x: rect.left,
      y: rect.bottom + 4,
      triggerRect: { top: rect.top, bottom: rect.bottom }
    });
    setStatusDropdownOpen(true);
  };

  const handleCheckboxTouchStart = (e) => {
    if (isSequentialDisabled) return;
    
    isLongPressingRef.current = false;
    longPressSucceededRef.current = false;
    
    const target = e.currentTarget;
    if (!target) return;
    
    const rect = target.getBoundingClientRect();
    
    longPressTimerRef.current = setTimeout(() => {
      isLongPressingRef.current = true;
      longPressSucceededRef.current = true;
      setStatusDropdownPosition({
        x: rect.left,
        y: rect.bottom + 4,
        triggerRect: { top: rect.top, bottom: rect.bottom }
      });
      setStatusDropdownOpen(true);
    }, 500);
  };

  const handleCheckboxTouchEnd = (e) => {
    // If long press succeeded, prevent the checkbox from toggling
    if (longPressSucceededRef.current) {
      e.preventDefault();
    }
    
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setTimeout(() => {
      isLongPressingRef.current = false;
      longPressSucceededRef.current = false;
    }, 100);
  };

  const handleCheckboxTouchCancel = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    isLongPressingRef.current = false;
    longPressSucceededRef.current = false;
  };

  const handleStatusSelect = (taskId, newStatus) => {
    isLongPressingRef.current = false;
    isSelectingStatusRef.current = true;
    setStatusDropdownOpen(false);
    
    // Position will be calculated after status update in the setTimeout below
    
    setTimeout(() => {
      onCheckboxChange(taskId, newStatus);
      if (onFocusTaskAfterStatusSelect && (newStatus === 'wip' || newStatus === 'waiting' || newStatus === 'wont_do')) {
        setTimeout(() => {
          onFocusTaskAfterStatusSelect(task._reactKey);
        }, 50);
      }
      setTimeout(() => {
        isSelectingStatusRef.current = false;
      }, 100);
    }, 10);
  };

  const handleStatusDropdownClose = () => {
    isLongPressingRef.current = false;
    longPressSucceededRef.current = false;
    setStatusDropdownOpen(false);
  };

  const handleCheckboxChangeWithFireworks = (taskId, newStatus) => {
    onCheckboxChange(taskId, newStatus);
  };

  return (
    <div className="checkbox-wrapper" ref={checkboxRef} data-task-checkbox>
      <label 
        className={`custom-checkbox text-non-selectable ${isSequentialDisabled ? 'custom-checkbox-disabled' : ''} ${task.status === 'wip' ? 'custom-checkbox-wip' : ''} ${task.status === 'waiting' ? 'custom-checkbox-waiting' : ''} ${task.status === 'wont_do' ? 'custom-checkbox-wont-do' : ''}`}
        onMouseEnter={() => {
          if (isSequentialDisabled) setBlockedTooltipVisible(true);
          else if (isIncomplete) setIncompleteTooltipVisible(true);
          else if (isCustomStatus) setCustomStatusTooltipVisible(true);
          if (isSequentialDisabled && onBlockedIconMouseEnter) onBlockedIconMouseEnter(task._reactKey);
        }}
        onMouseLeave={(e) => {
          setBlockedTooltipVisible(false);
          setIncompleteTooltipVisible(false);
          setCustomStatusTooltipVisible(false);
          handleCheckboxMouseLeave();
          if (isSequentialDisabled && onBlockedIconMouseLeave) {
            onBlockedIconMouseLeave();
          }
        }}
        onMouseDown={handleCheckboxMouseDown}
        onMouseUp={handleCheckboxMouseUp}
        onContextMenu={handleCheckboxContextMenu}
        onTouchStart={handleCheckboxTouchStart}
        onTouchEnd={handleCheckboxTouchEnd}
        onTouchCancel={handleCheckboxTouchCancel}
      >
        <input
          type="checkbox"
          className="task-checkbox-input"
          checked={task.status === 'complete' || task.status === 'wip' || task.status === 'waiting' || task.status === 'wont_do'}
          disabled={isSequentialDisabled}
          onChange={() => {
            if (!isSequentialDisabled && !statusDropdownOpen && !isLongPressingRef.current && !isSelectingStatusRef.current) {
              if (task.status === 'complete' || task.status === 'wont_do') {
                onCheckboxChange(task.id, 'incomplete');
              } else if (task.status === 'wip' || task.status === 'waiting') {
                handleCheckboxChangeWithFireworks(task.id, 'complete');
              } else {
                handleCheckboxChangeWithFireworks(task.id, 'complete');
              }
            }
          }}
        />
        <span className="custom-checkbox-box" ref={checkboxBoxRef}>
          {getStatusIcon(task.status)}
          {isIncomplete && !isSequentialDisabled && (
            <svg className="custom-checkbox-checkmark-preview" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M3 8 L6 11 L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          {!getStatusIcon(task.status) && isIncompleteWithFutureDueDate() && getClockIcon()}
          {isSequentialDisabled && (
            USE_UPWARD_ARROW_FOR_BLOCKED_TASKS ? (
              <svg className="custom-checkbox-disabled-line" viewBox="0 0 16 16" fill="none">
                <path d="M8 13 L8 3 M3 6 L8 3 L13 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg className="custom-checkbox-disabled-line" viewBox="0 0 16 16" fill="none">
                <line x1="3" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )
          )}
        </span>
      </label>
      {isSequentialDisabled && (
        <ToolbarStyleTooltip
          anchorRef={checkboxRef}
          visible={blockedTooltipVisible}
          text="Complete previous tasks first"
          placement="top"
          portal
        />
      )}
      {isIncomplete && !isSequentialDisabled && (
        <ToolbarStyleTooltip
          anchorRef={checkboxRef}
          visible={incompleteTooltipVisible && !statusDropdownOpen}
          text="Click to complete task"
          subtitle="Long-press to set custom status"
          placement="top"
          portal
          openDelay={1000}
        />
      )}
      {isCustomStatus && !isSequentialDisabled && (
        <ToolbarStyleTooltip
          anchorRef={checkboxRef}
          visible={customStatusTooltipVisible && !statusDropdownOpen}
          text={`Custom status: ${customStatusLabel}`}
          subtitle="Long-press to set custom status"
          placement="top"
          portal
          openDelay={1000}
        />
      )}
      {statusDropdownOpen && (
        <StatusDropdown
          taskId={task.id}
          currentStatus={task.status}
          onStatusSelect={handleStatusSelect}
          onClose={handleStatusDropdownClose}
          position={statusDropdownPosition}
        />
      )}
      <Fireworks trigger={fireworksTrigger} position={fireworksPosition} />
    </div>
  );
}

export default TaskCheckbox;

